import { IDexPair, IProjectPairShort } from 'types/pairs';

import {
  IAddPairArgs,
  ICreateMultiTransferPayload,
  ICreateMultiTransferResponse,
  IDescribedDexPair,
  IDescribeDexPairArgs,
  IMultiTransferTask,
  IPairWallet,
  IWithdraw,
  Job,
} from './models';

import { AApiBase } from '../apiBase/AApiBase';
import type { IBaseResponse } from '../apiBase/IBaseResponse';
import { mapError } from '../apiBase/mapError';
import { mapResponse } from '../apiBase/mapResponse';

class ApiPairs extends AApiBase {
  protected baseUrl = '/api';
  protected withHeader = true;

  private static __instance: ApiPairs | undefined;
  private static get instance(): ApiPairs {
    if (ApiPairs.__instance === undefined) {
      ApiPairs.__instance = new ApiPairs();
    }
    return ApiPairs.__instance;
  }

  public static getPairsByProjectId = async ({
    limit = 1000,
    offset = 0,
    sort_by,
    id,
  }: {
    limit?: number;
    offset?: number;
    sort_by?: string;
    id: number;
  }) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append('limit', limit.toString());
      urlParams.append('offset', offset.toString());
      if (sort_by) {
        urlParams.append('sort_by', sort_by);
      }

      const response = await ApiPairs.instance.get<IBaseResponse<{ items: IProjectPairShort[] }>>(
        `/projects/${id}/pairs` + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<{ items: IProjectPairShort[] }>({ error });
    }
  };

  public static addPair = async ({
    dex,
    cex,
    notes,
    token_base,
    token_quote,
    fee,
    id,
    swap_gas_limit,
    tradingType,
    pool_id,
    pair,
    integration_version,
  }: IAddPairArgs) => {
    try {
      // Send both names while the managers accept different versions of the
      // pool identifier. Both values describe the same address.
      const poolIdPayload =
        tradingType === 'dex'
          ? pool_id
            ? { pool_id, pair: pair ?? pool_id }
            : pair
              ? { pair }
              : {}
          : {};

      const response = await ApiPairs.instance.post<IBaseResponse<{ id: string }>>(
        `/projects/${id}/${tradingType}-pairs`,
        {
          ...(tradingType === 'dex'
            ? {
                dex,
                fee,
                ...(integration_version ? { integration_version } : {}),
                swap_gas_limit,
              }
            : { cex }),
          notes,
          token_base,
          token_quote,
          ...poolIdPayload,
        },
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{ id: string }>({ error });
    }
  };

  public static describeDexPair = async ({ id, dex, pair, token_base }: IDescribeDexPairArgs) => {
    try {
      const response = await ApiPairs.instance.post<IBaseResponse<IDescribedDexPair>>(
        `/projects/${id}/dex-pairs/describe`,
        {
          dex,
          pair,
          ...(token_base ? { token_base } : {}),
        },
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<IDescribedDexPair>({ error });
    }
  };

  public static getDexPairById = async (id: number) => {
    try {
      const response = await ApiPairs.instance.get<
        IBaseResponse<IDexPair & { address?: string; pair?: string }>
      >(`/pairs/${id}`);

      const data = ApiPairs.normalizeDexPair(response.data);

      return mapResponse({
        data,
      });
    } catch (error) {
      return mapError<IDexPair>({ error });
    }
  };

  private static normalizeDexPair = (
    data: IDexPair & { address?: string; pair?: string; pool_id?: string },
  ): IDexPair => {
    const pool_id = data.pool_id ?? data.address ?? data.pair;

    if (!pool_id) {
      throw new Error('Missing pool identifier in API response');
    }

    return { ...data, pool_id };
  };

  public static updateDexPair = async ({ id, notes }: { id: number; notes: string }) => {
    try {
      const response = await ApiPairs.instance.patch<IBaseResponse<any>>(`/pairs/${id}`, { notes });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static deleteDexPair = async (id: number) => {
    try {
      const response = await ApiPairs.instance.delete<IBaseResponse<any>>(`/pairs/${id}`);

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static getWalletsNotConnectedToPair = async ({
    pairId,
    limit,
    lastSeenId,
    wallets,
    bots_ids,
    balance,
    balance_usd,
    volume,
    volume_usd,
    transactions_count,
    is_disabled,
    not_connected,
  }: {
    pairId: number;
    limit?: number;
    lastSeenId?: number;
    wallets?: string[] | undefined;
    bots_ids?: string[] | undefined;
    balance?: string[] | undefined;
    balance_usd?: string[] | undefined;
    volume?: string[] | undefined;
    volume_usd?: string[] | undefined;
    transactions_count?: string[] | undefined;
    is_disabled?: string | undefined;
    not_connected?: string;
  }) => {
    type ReturnType = {
      has_next: boolean;
      items: {
        address: string;
        id: number;
        index: number;
      }[];
    };

    try {
      const urlParams = new URLSearchParams();

      limit && urlParams.append('limit', limit.toString());
      lastSeenId && urlParams.append('last_seen_id', lastSeenId.toString());
      not_connected && urlParams.append('not_connected', not_connected.toString());

      if (wallets) {
        for (const _wallet of wallets) {
          urlParams.append('wallets', _wallet);
        }
      }

      if (balance) {
        for (const _balance of balance) {
          urlParams.append('balance', _balance);
        }
      }
      if (balance_usd) {
        for (const _balance of balance_usd) {
          urlParams.append('balance_usd', _balance);
        }
      }

      if (volume) {
        for (const _volume of volume) {
          urlParams.append('volume', _volume);
        }
      }
      if (volume_usd) {
        for (const _volume of volume_usd) {
          urlParams.append('volume_usd', _volume);
        }
      }

      if (transactions_count) {
        for (const _tx_count of transactions_count) {
          urlParams.append('transactions_count', _tx_count);
        }
      }

      if (is_disabled) {
        urlParams.append('is_disabled', is_disabled);
      }

      if (bots_ids) {
        for (const _bots_id of bots_ids) {
          urlParams.append('bots_ids', _bots_id);
        }
      }

      const response = await ApiPairs.instance.get<IBaseResponse<ReturnType>>(
        `/pairs/${pairId}/wallets/not-connected` + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<ReturnType>({ error });
    }
  };

  public static getWalletsConnectedToPair = async ({
    pairId,
    limit,
    lastSeenId,
    wallets,
    bots_ids,
    balance,
    balance_usd,
    volume,
    volume_usd,
    transactions_count,
    is_disabled,
  }: {
    pairId: number;
    limit?: number;
    lastSeenId?: number;
    wallets?: string[] | undefined;
    bots_ids?: string[] | undefined;
    balance?: string[] | undefined;
    balance_usd?: string[] | undefined;
    volume?: string[] | undefined;
    volume_usd?: string[] | undefined;
    transactions_count?: string[] | undefined;
    is_disabled?: string | undefined;
  }) => {
    try {
      const urlParams = new URLSearchParams();

      limit && urlParams.append('limit', limit.toString());
      lastSeenId && urlParams.append('last_seen_id', lastSeenId.toString());

      if (wallets) {
        for (const _wallet of wallets) {
          urlParams.append('wallets', _wallet);
        }
      }

      if (balance) {
        for (const _balance of balance) {
          urlParams.append('balance', _balance);
        }
      }
      if (balance_usd) {
        for (const _balance of balance_usd) {
          urlParams.append('balance_usd', _balance);
        }
      }

      if (volume) {
        for (const _volume of volume) {
          urlParams.append('volume', _volume);
        }
      }
      if (volume_usd) {
        for (const _volume of volume_usd) {
          urlParams.append('volume_usd', _volume);
        }
      }

      if (transactions_count) {
        for (const _tx_count of transactions_count) {
          urlParams.append('transactions_count', _tx_count);
        }
      }

      if (is_disabled) {
        urlParams.append('is_disabled', is_disabled);
      }

      if (bots_ids) {
        for (const _bots_id of bots_ids) {
          urlParams.append('bots_ids', _bots_id);
        }
      }

      const response = await ApiPairs.instance.get<
        IBaseResponse<{ has_next: boolean; items: IPairWallet[]; total_connected_wallets: number }>
      >(`/pairs/${pairId}/wallets` + '?' + urlParams.toString());

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{
        has_next: boolean;
        items: IPairWallet[];
        total_connected_wallets: number;
      }>({ error });
    }
  };

  public static addWalletsToPair = async ({
    pairId,
    wallets,
  }: {
    pairId: number;
    wallets: { id: number; bots_ids: number[] }[];
  }) => {
    try {
      const response = await ApiPairs.instance.put<IBaseResponse<any>>(`/pairs/${pairId}/wallets`, {
        wallets,
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static deleteWalletsFromPair = async ({
    pairId,
    walletIds,
  }: {
    pairId: number;
    walletIds: number[];
  }) => {
    try {
      const response = await ApiPairs.instance.delete<any>(`/pairs/${pairId}/wallets`, {
        data: {
          wallets_ids: walletIds,
        },
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static getBackgroundJob = async ({ jobId }: { jobId: string }) => {
    try {
      const response = await ApiPairs.instance.get<IBaseResponse<Job>>(`/background-job/${jobId}`);

      return mapResponse({ data: response.data });
    } catch (error) {
      return mapError<Job>({ error });
    }
  };

  public static syncWallets = async ({ pairId, dryRun }: { pairId: number; dryRun: boolean }) => {
    try {
      const response = await ApiPairs.instance.post<{ job_id: string }>(
        `/pairs/${pairId}/sync-wallets`,
        { dryRun },
      );

      return mapResponse({ data: response.data });
    } catch (error) {
      return mapError<{ job_id: string }>({ error });
    }
  };

  public static deposit = async ({
    transactionHashes,
    pairId,
  }: {
    transactionHashes: string[];
    pairId: number;
  }) => {
    try {
      const response = await ApiPairs.instance.post<any>(`/pairs/${pairId}/deposit`, {
        transactions: transactionHashes,
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static withdraw = async ({
    recipient_address,
    pairId,
    transfers,
  }: {
    recipient_address: string;
    pairId: number;
    transfers: {
      amount: string | undefined;
      max_amount: boolean;
      token_id: number;
      wallet_id: number;
    }[];
  }) => {
    try {
      const response = await ApiPairs.instance.post<IWithdraw>(`/pairs/${pairId}/withdraw`, {
        recipient_address,
        transfers,
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<IWithdraw>({ error });
    }
  };

  public static walletsImport = async ({
    pairId,
    botsIds,
    wallets,
  }: {
    pairId: number;
    botsIds: number[];
    wallets: {
      address: string;
      private_key: string;
    }[];
  }) => {
    try {
      const response = await ApiPairs.instance.post<IBaseResponse<any>>(
        `/pairs/${pairId}/wallets-import`,
        { bots_ids: botsIds, wallets },
      );

      return mapResponse({ data: response.data });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static createMultiTransferTask = async ({
    pairId,
    payload,
  }: {
    pairId: number;
    payload: ICreateMultiTransferPayload;
  }) => {
    try {
      const response = await ApiPairs.instance.post<IBaseResponse<ICreateMultiTransferResponse>>(
        `/pairs/${pairId}/multi-transfer`,
        payload,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<ICreateMultiTransferResponse>({ error });
    }
  };

  public static getMultiTransferTasks = async (pairId: number) => {
    try {
      const response = await ApiPairs.instance.get<IBaseResponse<{ items: IMultiTransferTask[] }>>(
        `/pairs/${pairId}/multi-transfer`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{ items: IMultiTransferTask[] }>({ error });
    }
  };
}

export { ApiPairs };
